import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHero } from "../components/Sections";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { EnquiryDialog } from "../components/LeadForm";
import { Button } from "../components/ui/button";
import { IndianRupee, Info } from "lucide-react";

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

function EmiCalculator() {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(5);

  const { emi, totalInterest, total } = useMemo(() => {
    const p = amount, r = rate / 12 / 100, n = years * 12;
    if (r === 0) return { emi: p / n, totalInterest: 0, total: p };
    const e = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const t = e * n;
    return { emi: e, totalInterest: t - p, total: t };
  }, [amount, rate, years]);

  return (
    <div className="grid gap-8 lg:grid-cols-5">
      <Card className="border p-6 lg:col-span-3">
        <h3 className="font-heading text-lg font-bold">EMI Calculator</h3>
        <div className="mt-6 space-y-8">
          <div>
            <div className="flex items-center justify-between">
              <Label>Loan Amount</Label>
              <span className="font-semibold text-primary">{inr(amount)}</span>
            </div>
            <Slider className="mt-3" min={50000} max={10000000} step={50000} value={[amount]} onValueChange={(v) => setAmount(v[0])} />
            <div className="mt-1 flex justify-between text-xs text-muted-foreground"><span>₹50K</span><span>₹1Cr</span></div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Interest Rate (p.a.)</Label>
              <span className="font-semibold text-primary">{rate}%</span>
            </div>
            <Slider className="mt-3" min={5} max={30} step={0.25} value={[rate]} onValueChange={(v) => setRate(v[0])} />
            <div className="mt-1 flex justify-between text-xs text-muted-foreground"><span>5%</span><span>30%</span></div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Tenure (years)</Label>
              <span className="font-semibold text-primary">{years} yr</span>
            </div>
            <Slider className="mt-3" min={1} max={30} step={1} value={[years]} onValueChange={(v) => setYears(v[0])} />
            <div className="mt-1 flex justify-between text-xs text-muted-foreground"><span>1</span><span>30</span></div>
          </div>
        </div>
      </Card>

      <Card className="border p-6 lg:col-span-2 bg-secondary/40">
        <p className="text-sm text-muted-foreground">Monthly EMI (estimate)</p>
        <p className="font-heading text-3xl font-extrabold text-primary flex items-center">{inr(emi)}</p>
        <div className="mt-6 space-y-4">
          <div className="flex justify-between border-b pb-3"><span className="text-sm text-muted-foreground">Principal</span><span className="font-semibold">{inr(amount)}</span></div>
          <div className="flex justify-between border-b pb-3"><span className="text-sm text-muted-foreground">Total Interest</span><span className="font-semibold">{inr(totalInterest)}</span></div>
          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Total Payment</span><span className="font-semibold">{inr(total)}</span></div>
        </div>
        <p className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
          Estimate only. Actual EMI, rate and charges are determined by the lender.
        </p>
        <div className="mt-4">
          <EnquiryDialog trigger={<Button className="w-full">Apply Now</Button>} defaultType="Personal Loan" />
        </div>
      </Card>
    </div>
  );
}

function GstCalculator() {
  const [amount, setAmount] = useState(10000);
  const [rate, setRate] = useState("18");
  const [mode, setMode] = useState("add");

  const r = Number(rate) / 100;
  const gst = mode === "add" ? amount * r : amount - amount / (1 + r);
  const base = mode === "add" ? amount : amount - gst;
  const totalV = mode === "add" ? amount + gst : amount;

  return (
    <div className="grid gap-8 lg:grid-cols-5">
      <Card className="border p-6 lg:col-span-3">
        <h3 className="font-heading text-lg font-bold">GST Calculator</h3>
        <div className="mt-6 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="gst-amt">Amount (₹)</Label>
            <Input id="gst-amt" type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value) || 0)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>GST Rate</Label>
              <Select value={rate} onValueChange={setRate}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["3", "5", "12", "18", "28"].map((x) => <SelectItem key={x} value={x}>{x}%</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add GST</SelectItem>
                  <SelectItem value="remove">Remove GST</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Card>
      <Card className="border p-6 lg:col-span-2 bg-secondary/40">
        <p className="text-sm text-muted-foreground">Total GST</p>
        <p className="font-heading text-3xl font-extrabold text-primary">{inr(gst)}</p>
        <div className="mt-6 space-y-4">
          <div className="flex justify-between border-b pb-3"><span className="text-sm text-muted-foreground">Base Amount</span><span className="font-semibold">{inr(base)}</span></div>
          <div className="flex justify-between border-b pb-3"><span className="text-sm text-muted-foreground">GST ({rate}%)</span><span className="font-semibold">{inr(gst)}</span></div>
          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Total Amount</span><span className="font-semibold">{inr(totalV)}</span></div>
        </div>
      </Card>
    </div>
  );
}

export default function Calculators() {
  const { type } = useParams();
  const navigate = useNavigate();
  const tab = type === "gst" ? "gst" : "emi";
  return (
    <div>
      <PageHero title="Tools & Calculators"
        subtitle="Plan responsibly with our EMI and GST calculators. Results are indicative estimates."
        crumbs={[{ label: "Tools" }, { label: tab === "gst" ? "GST Calculator" : "EMI Calculator" }]} />
      <section className="section-pad">
        <div className="container-page">
          <Tabs value={tab} onValueChange={(v) => navigate(`/calculators/${v}`)} className="mb-8">
            <TabsList>
              <TabsTrigger value="emi">EMI Calculator</TabsTrigger>
              <TabsTrigger value="gst">GST Calculator</TabsTrigger>
            </TabsList>
          </Tabs>
          {tab === "gst" ? <GstCalculator /> : <EmiCalculator />}
        </div>
      </section>
    </div>
  );
}
